"use client";

import { useState } from "react";
import { useAction } from "convex/react";
import { api } from "@convex/_generated/api";
import { handleError } from "@/lib/handle-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function useNewsletterSubscription() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const subscribeToNewsletter = useAction(api.newsletter.actions.subscribe);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await subscribeToNewsletter({ email, source: "footer" });
      if (result.success) {
        setConfirmation(
          result.status === "active" ? "Subscribed." : "Check your email to confirm.",
        );
      } else {
        setError(result.message);
      }
    } catch (err) {
      const handled = handleError(err, { display: "silent" });
      if (handled.action !== "ignore") {
        setError(handled.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return { email, setEmail, isSubmitting, confirmation, error, handleSubmit };
}

export function FooterNewsletter() {
  const { email, setEmail, isSubmitting, confirmation, error, handleSubmit } = useNewsletterSubscription();
  return (
    <div>
      <h2 className="mb-2 text-xs font-semibold text-muted-foreground">Newsletter</h2>
      {confirmation ? (
        <p role="status" className="text-sm text-success-strong">{confirmation}</p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">Monthly pricing trends & insights.</p>
          <form onSubmit={handleSubmit} className="mt-2">
            <div className="flex gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="your@company.com"
                  required
                  disabled={isSubmitting}
                  className="border-border"
                  aria-label="Email address"
                  size="default"
                />
              </div>
              <Button variant="primary" type="submit" disabled={isSubmitting} size="default">
                {isSubmitting ? "..." : "Join"}
              </Button>
            </div>
            {error && <p className="mt-1 text-xs text-destructive-strong">{error}</p>}
          </form>
        </>
      )}
    </div>
  );
}
